<template>
  <div class="min-w-0 space-y-6">
    <div class="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <div class="text-sm font-semibold text-slate-700">Reports</div>
        <h2 class="mt-1 text-2xl font-bold tracking-tight text-slate-900">Revenue & statistics</h2>
      </div>
      <div class="rounded-lg bg-white px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200">
        Today: <span class="font-semibold text-slate-900">{{ fmt(todayRevenue) }}</span> ETB • Month:
        <span class="font-semibold text-slate-900">{{ fmt(monthRevenue) }}</span> ETB
      </div>
    </div>

    <div class="grid min-w-0 gap-6 lg:grid-cols-2">
      <section class="min-w-0 max-w-full rounded-2xl bg-white p-5 ring-1 ring-slate-200">
        <div class="text-sm font-bold text-slate-900">Revenue by day (recent)</div>
        <div class="mt-1 text-xs text-slate-500">From guest payments collected at exit.</div>
        <div
          class="mt-4 max-w-full overflow-x-auto overscroll-x-contain rounded-xl ring-1 ring-slate-200 [-webkit-overflow-scrolling:touch]"
        >
          <table class="w-full min-w-[520px] text-left text-xs">
            <thead class="bg-slate-50 text-slate-600">
              <tr>
                <th class="px-3 py-2 font-semibold">Day</th>
                <th class="px-3 py-2 font-semibold">Payments</th>
                <th class="px-3 py-2 font-semibold text-right">Amount (ETB)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in dailyRows" :key="r.dayKey" class="border-t border-slate-200">
                <td class="px-3 py-2 font-semibold text-slate-900">{{ r.dayKey }}</td>
                <td class="px-3 py-2 text-slate-600">{{ r.count }}</td>
                <td class="px-3 py-2 text-right font-semibold text-slate-900 tabular-nums">{{ fmt(r.amount) }}</td>
              </tr>
              <tr v-if="dailyRows.length === 0">
                <td class="px-3 py-8 text-center text-slate-500" colspan="3">No payments yet.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="min-w-0 max-w-full rounded-2xl bg-white p-5 ring-1 ring-slate-200">
        <div class="text-sm font-bold text-slate-900">Operator performance</div>
        <div class="mt-1 text-xs text-slate-500">Completed sessions and collected revenue.</div>
        <div
          class="mt-4 max-w-full overflow-x-auto overscroll-x-contain rounded-xl ring-1 ring-slate-200 [-webkit-overflow-scrolling:touch]"
        >
          <table class="w-full min-w-[520px] text-left text-xs">
            <thead class="bg-slate-50 text-slate-600">
              <tr>
                <th class="px-3 py-2 font-semibold">Operator</th>
                <th class="px-3 py-2 font-semibold">Completed</th>
                <th class="px-3 py-2 font-semibold text-right">Revenue (ETB)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in operatorRows" :key="r.operatorId" class="border-t border-slate-200">
                <td class="px-3 py-2 font-semibold text-slate-900">{{ r.name }}</td>
                <td class="px-3 py-2 text-slate-600">{{ r.completed }}</td>
                <td class="px-3 py-2 text-right font-semibold text-slate-900 tabular-nums">{{ fmt(r.revenue) }}</td>
              </tr>
              <tr v-if="operatorRows.length === 0">
                <td class="px-3 py-8 text-center text-slate-500" colspan="3">No completed sessions yet.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <section class="min-w-0 max-w-full rounded-2xl bg-white p-5 ring-1 ring-slate-200">
      <div class="text-sm font-bold text-slate-900">History (completed sessions)</div>
      <div class="mt-1 text-xs text-slate-500">All completed sessions (guest + permanent).</div>

      <div
        class="mt-4 max-w-full overflow-x-auto overscroll-x-contain rounded-xl ring-1 ring-slate-200 [-webkit-overflow-scrolling:touch]"
      >
        <table class="w-full min-w-[720px] text-left text-xs">
          <thead class="bg-slate-50 text-slate-600">
            <tr>
              <th class="px-3 py-2 font-semibold">Plate</th>
              <th class="px-3 py-2 font-semibold">Type</th>
              <th class="px-3 py-2 font-semibold">Entry</th>
              <th class="px-3 py-2 font-semibold">Exit</th>
              <th class="px-3 py-2 font-semibold text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in completed.slice(0, 30)" :key="s.id" class="border-t border-slate-200">
              <td class="px-3 py-2 font-semibold text-slate-900">{{ s.plateNumber }}</td>
              <td class="px-3 py-2">
                <span class="rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1"
                  :class="s.type === 'permanent' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-sky-50 text-sky-700 ring-sky-200'">
                  {{ s.type }}
                </span>
              </td>
              <td class="px-3 py-2 text-slate-600">{{ dt(s.entryTime) }}</td>
              <td class="px-3 py-2 text-slate-600">{{ dt(s.exitTime) }}</td>
              <td class="px-3 py-2 text-right font-semibold text-slate-900 tabular-nums">{{ fmt(s.amount) }} ETB</td>
            </tr>
            <tr v-if="completed.length === 0">
              <td class="px-3 py-8 text-center text-slate-500" colspan="5">No history yet.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";

const store = useStore();

const todayRevenue = computed(() => store.getters.todayRevenue);
const monthRevenue = computed(() => store.getters.monthRevenue);
const completed = computed(() => store.getters.completedSessions);
const payments = computed(() => store.state.payments);

const dailyRows = computed(() => {
  const map = new Map();
  for (const p of payments.value) {
    const k = p.dayKey;
    const prev = map.get(k) || { dayKey: k, amount: 0, count: 0 };
    prev.amount += Number(p.amount) || 0;
    prev.count += 1;
    map.set(k, prev);
  }
  return Array.from(map.values()).sort((a, b) => String(b.dayKey).localeCompare(String(a.dayKey))).slice(0, 14);
});

const operatorRows = computed(() => {
  const byId = new Map();
  const staff = store.state.staffUsers;
  const nameFor = (id) => staff.find((u) => u.id === id)?.name || "Unknown";

  for (const s of completed.value) {
    const opId = s.operatorExitId || s.operatorEntryId || "unknown";
    const prev = byId.get(opId) || { operatorId: opId, name: nameFor(opId), completed: 0, revenue: 0 };
    prev.completed += 1;
    prev.revenue += Number(s.amount) || 0;
    byId.set(opId, prev);
  }
  return Array.from(byId.values()).sort((a, b) => b.revenue - a.revenue);
});

function fmt(v) {
  const n = Number(v) || 0;
  return n.toFixed(2);
}

function dt(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
</script>

